<template>
  <div>
    <div
      class="row m-0 list-heading mt-3 list-manipulations"
      data-v-step="campaignsView.view.main">

      <div class="col m-0 p-0 centered">

        <!-- Date tabs -->
        <div class="date-tabs-container">
          <div class="header-date-tabs">
            <slot
              name="grid-date-prefix"
              class="slot grid-date-prefix" />

            <input-date-range
              v-if="showRangePicker"
              class="dr-input"
              small
              slim
              transparent
              inputName="dateRange"
              :inputValue="getActiveDate"
              :dateRangePickerPresets="dateRangePickerPresets"
              @fieldUpdate="changeDate" />

              <slot name="extraFilters" />
          </div>
        </div>

        <!-- Mpe grid manipulation items -->
        <div
          class="list-manipulation-action-container"
          data-v-step="campaignsView.view.calendar">

          <div class="mr-3">
            <input-button
              variant="plainText"
              btnStyle="link-style"
              :inputPlaceholder="$t('Button.week')"
              :active="getDisplayStyle === 'agendaWeek'"
              @click="setDisplayStyle('agendaWeek')" />

            <input-button
              variant="plainText"
              btnStyle="link-style"
              :inputPlaceholder="$t('Button.month')"
              :active="getDisplayStyle === 'month'"
              @click="setDisplayStyle('month')" />

          </div>

          <div class="ml-3 space-top grid-manipulators">
            <q-icon
              class="c-pointer"
              :active="getDisplayStyle === 'list'"
              :allowSelfUpdate="false"
              width="20px"
              height="20px"
              pack="leftMenu"
              name="listGrey"
              altPack="leftMenu"
              altName="listGreen"
              @click="setDisplayStyle('list')" />

            <q-icon
              class="c-pointer"
              :active="getDisplayStyle === 'grid'"
              :allowSelfUpdate="false"
              style="margin-left: 13px;"
              width="20px"
              height="20px"
              pack="leftMenu"
              name="gridGrey"
              altPack="leftMenu"
              altName="gridGreen"
              @click="setDisplayStyle('grid')" />

          </div>

        </div>
      </div>
    </div>

    <div class="spacer-ceditor d-none d-md-block" />
    <div class="spacer-ceditor-smaller d-block d-md-none" />

    <div class="row selected-posts pt-0 ml-0 mr-0">
      <!-- Mpe grid display -->
      <div class="w-100">
        <mpe-grid
          ref="mpeGrid"
          @editMpe="editMpe"
          @removeMpe="removeMpe"
          @setSchedule="setSchedule"
          @duplicate="duplicate"
          @changeDate="changeDate"
          :validRange="getValidRange"
          :campaignPreviewItems="campaignPreviewItems"
          :useCalendarNavigation="useCalendarNavigation"
          :listStyle="getDisplayStyle"
          :listItems="listItems"
          :variant="variant"
          :removing="removing"
          :viewedMonth="getViewedMonth" />

      </div>
    </div>
  </div>
</template>

<script>
import MpeGrid from '@/components/service/campaignManager/MpeGrid'
import InputButton from '@/components/standard/input/InputButton'
import QIcon from '@/components/standard/display/QIcon'

import InputDateRange from '@/components/standard/input/InputDateRange'

import resolver from '@/assets/modules/error/resolver'

import moment from 'moment'

export default {
  data () {
    return {
      calendarLabel: null
    }
  },
  methods: {
    /**
     * Requests a MPE edit
     * @param {Object} e
     */
    editMpe (e) {
      this.$emit('editMpe', e)
    },

    /**
     * Requests a MPE remove
     * @param {Object} e
     */
    removeMpe (e) {
      this.$emit('removeMpe', e)
    },

    /**
     * Requests a MPE duplication
     * @param {Object} e
     */
    duplicate (e) {
      this.$emit('duplicate', e)
    },

    /**
     * Changes a MPE schedule
     * @param {Object} e
     */
    setSchedule (e) {
      this.$emit('setSchedule', e)
    },

    /**
     * changes a MPE date
     * @param {Object} e
     */
    changeDate (e) {
      this.$emit('changeDate', e)
    },
    
    /**
     * Sets the new display style for the grid
     * @param {String} style
     */
    setDisplayStyle (style) {
      this.$emit('setDisplayStyle', style)
    }
  },

  computed: {
    getValidRange () {
      let rtr = {}
      let noLimit = true
      if (this.minDate) {
        rtr.start = this.minDate
        noLimit = false
      }

      if (this.maxDate) {
        rtr.end = this.maxDate
        noLimit = false
      }

      return (noLimit) ? null : rtr
    },

    /**
     * Tells if range picker is visible
     * @returns {Boolean}
     */
    showRangePicker () {
      if (this.allowRangePicker && !resolver.isCalendarView(this.getDisplayStyle)) return true
    },

    /**
     * Tells the size of the date step
     * @returns {String}
     */
    getDateStep () {
      return resolver.getDateStepSize(this.getDisplayStyle)
    },

    /**
     * Gives default date for calendar view
     * @returns {String}
     */
    getDefaultDate () {
      return (this.dateList[0] || {}).id
    },

    /**
     * Tells what date to use with our calendar. If any is selected use that, otherwise
     * use the default one
     * @returns {String}
     */
    getViewedMonth () {
      return this.getActiveDate.id || this.getDefaultDate
    },

    getActiveDate () {
      // Viewed date has not yet been selecred.
      if (!this.activeDate.id) {
        return {
          id: null
        }
      }
      return this.activeDate
    },

    /**
     * Gives the selected display style
     * @returns {String}
     */
    getDisplayStyle () {
      return this.defaultStyle
    },

    /**
     * Gives display items
     * @returns {Array}
     */
    getDisplayList () {
      return this.listItems
    }
  },

  props: {
    removing: {
      type: Object,
      required: false,
      default: () => { return {} }
    },
    dateRangePickerPresets: {
      type: Object,
      required: false,
      default: null
    },
    useCalendarNavigation: {
      type: Boolean,
      required: false,
      default: false
    },
    allowRangePicker: {
      type: Boolean,
      required: false,
      default: false
    },
    campaignPreviewItems: {
      type: Boolean,
      required: false,
      default: false
    },
    defaultStyle: {
      type: String,
      required: false,
      default: 'list'
    },
    listItems: {
      type: Array,
      required: false,
      default: () => []
    },
    variant: {
      type: String
    },
    dataSource: {
      type: String,
      required: true,
      default: 'campaign'
    },
    activeDate: {
      type: Object,
      required: false,
      default: () => {return {}}
    },
    today: {
      required: true,
      default: null
    },
    minDate: {
      type: String,
      required: false,
      default: null
    },
    maxDate: {
      type: String,
      required: false,
      default: null
    },
    dateList: {
      type: Array,
      required: false,
      default: () => []
    }
  },

  components: {
    'q-icon': QIcon,
    'input-button': InputButton,
    'mpe-grid': MpeGrid,
    'input-date-range': InputDateRange
  }
}
</script>

<style scoped>
.spacer-ceditor {
  margin-top: 24px;
}
.spacer-ceditor-smaller {
  margin-top: 5px;
}
.date-tabs-container {
  min-width: 200px;
  flex-grow: 100;
}
.date-tabs-container.date-tabs {
  max-width: Calc(100% - 250px);
}
.list-manipulation-action-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  flex-wrap: wrap;
  padding: 0 10px;
  margin-bottom: 6px;
}
.grid-manipulators {
  margin-right: 15px;
}
.list-manipulations {
  border: 1px solid #E3E3E3;
  min-height: 50px;
  /* position: sticky;
  top: 0px;
  z-index: 100;
  background-color: #f8f8f8; */
}
.list-manipulations .centered {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
}

.selected-posts {
  background-color: #FFF;
}

.c-pointer {
  cursor: pointer;
}

.header-date-tabs {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
}

.dr-input {
  width: 100%;
  max-width: 200px;
}

@media (max-width: 768px) {
  .date-tabs-container, .date-tabs-container.date-tabs {
    max-width: unset;
  }
  .list-manipulations {
    border-bottom: none;
    border-left: none;
    border-right: none;
  }
  .list-heading {
    margin-bottom: 11px!important;
  }
}

</style>



// WEBPACK FOOTER //
// src/components/standard/display/CampaignGrid.vue?604eaab2